import { useEffect } from 'react';
import {
  analyticsAvailable,
  analyticsOn,
  trackSectionViewed,
  type LandingSection,
} from '../services/analytics';

/**
 * Reports each `[data-track-section]` once per visit, when the visitor actually gets
 * there: its top reaches the upper half of the viewport, or it sits fully in view at
 * the bottom of the page (a short last section never climbs that high). A section
 * that is skipped — jumped over by an anchor link — is not counted.
 *
 * A passive, frame-throttled scroll check rather than IntersectionObserver: "top
 * above the middle" isn't a visibility ratio, and the hero's pinned scroll story
 * makes ratios meaningless for tall sections anyway.
 */
export function useSectionViews(): void {
  useEffect(() => {
    if (!analyticsAvailable) return;
    const pending = new Map<Element, LandingSection>();
    document
      .querySelectorAll<HTMLElement>('[data-track-section]')
      .forEach((el) => pending.set(el, el.dataset.trackSection as LandingSection));

    let frame = 0;
    const check = () => {
      frame = 0;
      // Nothing is crossed off before consent; see useScrollDepth.
      if (!analyticsOn()) return;
      const vh = window.innerHeight;
      for (const [el, section] of pending) {
        const { top, bottom } = el.getBoundingClientRect();
        const reached = (top < vh / 2 && bottom > 0) || (top >= 0 && bottom <= vh);
        if (!reached) continue;
        pending.delete(el);
        trackSectionViewed(section);
      }
      if (pending.size === 0) window.removeEventListener('scroll', onScroll);
    };
    const onScroll = () => {
      frame ||= requestAnimationFrame(check);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    check();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
}
